import { useEffect, useRef, useState } from "react";
import "react-vertical-timeline-component/style.min.css";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  createProductReview,
  getProductReview,
} from "../redux/actions/productReviewAction";
import { exchangeOrder } from "../redux/actions/exchangeOrderAction";
import * as Alert from "../components/common/Alert";
import Timeline from "../components/other/Timeline";
import { useDownloadInvoice, useOrderDetails } from "../hooks/order";

export default function Track() {
  const navigate = useNavigate();
  const {
    orderDetails: orderData,
    loading,
    error,
    refetch,
  } = useOrderDetails();
  const {
    loading: fetchingInvoice,
    error: invoiceError,
    getInvoice,
  } = useDownloadInvoice();

  const [exchangeIntiated, setExchangeInitiated] = useState(false);

  const hiddenLink = useRef(null);

  const handleDownload = async () => {
    try {
      const data = await getInvoice();
      const a = document.createElement("a");
      a.href = data.invoice_url;
      // a.download = "performa_invoice";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {}
  };

  useEffect(() => {
    if (exchangeIntiated) {
      refetch();
    }
  }, [exchangeIntiated]);

  if (loading) {
    return <p className="min-h-screen">Loading...</p>;
  }

  if (!orderData) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center text-center px-4">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">
          No Order Found
        </h2>
        <p className="text-gray-500 mb-4">
          We couldn't retrieve your order details. Please go back and try again.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 transition"
        >
          Go Back
        </button>
      </div>
    );
  }
  const { products = [] } = orderData;

  return (
    <div className="min-h-screen w-full flex flex-col items-center pb-4">
      <div className="w-full max-w-6xl">
        {/* Product Header */}
        <div className="relative w-full bg-gradient-to-b from-pink-300 to-white p-6">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-5 left-5 bg-white p-2 rounded-full shadow hover:bg-pink-100 transition"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Order Info Header */}
          <div className="mt-12 text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Order Tracking</h1>
            <p className="text-gray-600">Order #{orderData.order_no}</p>
            <p className="text-sm text-gray-500">
              Status: {orderData.order_status}
            </p>
          </div>
          {orderData.order_status !== "Cancelled" && (
            <Timeline currentStatus={orderData.order_status} />
          )}

          <div className="mt-8 w-full max-w-6xl mx-auto space-y-4">
            {(() => {
              // Flatten all sub-products from all products into a single array
              const allSubProducts = [];
              let totalMrp = 0;
              let totalSellingPrice = 0;

              products.forEach((product) => {
                if (
                  product.ordered_sub_products &&
                  product.ordered_sub_products.length > 0
                ) {
                  product.ordered_sub_products.forEach((orderedSubProduct) => {
                    const subProductDetails =
                      orderedSubProduct.sub_product_details;

                    // Calculate totals
                    totalMrp +=
                      orderedSubProduct.product_price *
                      orderedSubProduct.quantity;
                    totalSellingPrice +=
                      orderedSubProduct.product_price *
                      orderedSubProduct.quantity;

                    allSubProducts.push({
                      product: product,
                      orderedSubProduct: orderedSubProduct,
                      subProductDetails: subProductDetails,
                    });
                  });
                }
              });

              return (
                <>
                  {allSubProducts.length > 0 ? (
                    allSubProducts.map((item, index) => {
                      const { product, orderedSubProduct, subProductDetails } =
                        item;

                      return (
                        <div
                          key={`${subProductDetails.sub_pro_id}-${index}`}
                          className="flex flex-col items-start justify-between bg-white shadow-md border border-gray-200 rounded-lg p-4 gap-4"
                        >
                          <div className="flex items-start gap-4 w-full">
                            <img
                              loading="lazy"
                              src={
                                subProductDetails.sub_pro_img ||
                                product.product_primary_img ||
                                "/fallback.png"
                              }
                              onError={(e) =>
                                (e.currentTarget.src = "/fallback.png")
                              }
                              alt={product.product_name || "Product"}
                              onClick={() =>
                                navigate(`/ProductsDetails/${product.pro_id}`)
                              }
                              className="w-28 h-36 object-cover rounded border cursor-pointer transition-transform ease-in-out duration-300 hover:scale-110"
                            />
                            <div className="flex flex-col flex-grow">
                              <p className="font-semibold text-gray-800">
                                {product.product_name}
                              </p>
                              <p className="text-gray-500 text-sm mt-1">
                                Size: {subProductDetails.size}
                              </p>
                              <p className="text-gray-500 text-sm">
                                Color: {subProductDetails.color || "N/A"}
                              </p>
                              {/* <p className="text-gray-600 text-sm">
                                Type: {subProductDetails.selling_type} ({subProductDetails.no_of_pcs} pcs)
                              </p> */}
                              <p className="text-gray-600 text-sm">
                                Quantity: {orderedSubProduct.quantity}
                              </p>
                              {/* <p className="text-gray-600 text-sm">
                                Location: {subProductDetails.location}
                              </p>
                              <p className="text-gray-600 text-sm">
                                SKU: {subProductDetails.sub_pro_sr_no}
                              </p> */}
                              <p className="text-black font-bold mt-2">
                                ₹{orderedSubProduct.product_price}
                              </p>
                              {subProductDetails.customer_product_discount >
                                0 && (
                                <p className="text-gray-500 text-sm line-through">
                                  MRP: ₹{subProductDetails.customer_mrp}
                                </p>
                              )}
                              <div className="hidden sm:block">
                                <hr className="my-2" />
                                <RatingSection productId={product.pro_id} />
                                <hr className="my-2" />
                              </div>
                            </div>
                          </div>
                          <div className="sm:hidden w-full">
                            <hr className="my-2" />
                            <RatingSection productId={product.pro_id} />
                            <hr className="my-2" />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-gray-600 mt-4 border border-gray-300 p-4 rounded bg-gray-100">
                      No items found in this order.
                    </div>
                  )}

                    {/* Shipping Details */}
                    {orderData.order_status === "Shipped" ||
                    (orderData.order_status === "Completed" && (
                      <div className="mt-4 bg-white rounded-lg shadow-md border border-gray-200 p-4">
                        <h3 className="font-semibold text-gray-800 mb-3">
                          Shipping Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p className="flex justify-between">
                            <span>Shipped Date:</span>
                            <span>
                              {new Date(orderData.shipping_info?.dispatch_date).toDateString()}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span>Shipping Address:</span>
                            <span>{orderData.shipping_address}</span>
                          </p>
                          <p className="flex justify-between">
                            <span>Shipping Charges:</span>
                            <span>₹{orderData.shipping_charges}</span>
                          </p>
                          <p className="flex justify-between">
                            <span>Dispatch Through:</span>
                            <span>{orderData.shipping_info?.dispatch_through ?? "N/A"}</span>
                          </p>
                          <p className="flex justify-between">
                            <span>Tracking ID:</span>
                            <span>{orderData.shipping_info?.tracking_id ?? "N/A"}</span>
                          </p>
                          <p className="flex justify-between">
                            <span>Contact Details:</span>
                            <span>{orderData.shipping_info?.contact_details ?? "N/A"}</span>
                          </p>
                          <p className="flex justify-between">
                            <span>Remark:</span>
                            <span>{orderData.shipping_info?.shipping_remark ?? "N/A"}</span>
                          </p>
                        </div>
                      </div>
                    ))}

                  {/* Delivery Details */}
                  {orderData.order_status === "Completed" && (
                    <div className="mt-4 bg-white rounded-lg shadow-md border border-gray-200 p-4">
                      <h3 className="font-semibold text-gray-800 mb-3">
                        Delivery Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p className="flex justify-between">
                          <span>Delivery Date:</span>
                          {new Date(orderData.delivery_date).toDateString()}
                        </p>
                        <p className="flex justify-between">
                          <span>Remark:</span>
                          <span>
                            {orderData.delivery_info.remark_text ?? "N/A"}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Order Summary */}
                  <div className="mt-4 bg-white rounded-lg shadow-md border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">
                      Order Summary
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal (MRP):</span>
                        <span>₹{totalMrp}</span>
                      </div>
                      <div className="flex justify-between text-green-500">
                        <span>Discount:</span>
                        <span>- ₹{orderData.discount_amt}</span>
                      </div>
                      <div className="flex justify-between text-red-500">
                        <span>Shipping:</span>
                        <span>+ ₹{orderData.shipping_charges}</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>₹{orderData.amount}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Payment Method:</span>
                        <span>{orderData.payment_type}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Payment Status:</span>
                        <span>{orderData.payment_status}</span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {orderData.order_status === "Completed" && (
          <ExchangeSection
            orderId={orderData.order_id}
            orderDate={orderData.created_at}
            setExchangeInitiated={setExchangeInitiated}
          />
        )}

        <button
          disabled={fetchingInvoice}
          onClick={handleDownload}
          className="ml-5 mr-auto mt-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
        >
          {fetchingInvoice ? "Downloding..." : "Download Invoice"}
          {/* <a
              href={orderData.invoice_pdf}
              ref={hiddenLink}
              download
              className="hidden"
              rel="noopener noreferrer"
              target="_blank"
            >
              Download
            </a> */}
        </button>
        {/* Dynamic Timeline */}
        {/* {renderTimeline()} */}
      </div>
    </div>
  );
}

const RatingSection = ({ productId }) => {
  const dispatch = useDispatch();
  // const productReview = useSelector((state) => state.getProductReview?.data);
  const productReview = useSelector(
    (state) => state.getProductReview?.data[productId]
  );

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitReview = async (productId) => {
    if (productReview.user_has_rated) {
      toast.error("Review cannot be added!");
    }
    if (rating === 0) {
      toast.info("Please select a rating");
      return;
    }

    setIsSubmitting(true);

    try {
      // Replace this with your actual API call
      const reviewData = {
        product_id: productId,
        rating: rating,
        review: reviewComment,
      };

      dispatch(createProductReview(reviewData));
    } catch (error) {
    } finally {
      setTimeout(() => {
        dispatch(getProductReview(productId));
        setIsSubmitting(false);
      }, 2000);
    }
  };
  useEffect(() => {
    if (productId) {
      dispatch(getProductReview(productId));
    }
  }, [dispatch, productId]);

  return (
    <>
      <p className="text-gray-500 text-sm">Rate this product</p>
      <div className="flex flex-col md:flex-row md:items-center gap-1">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => {
            const activeRating = productReview?.user_has_rated
              ? productReview?.user_rating
              : hoveredRating || rating;
            return (
              <button
                key={star}
                type="button"
                disabled={productReview?.user_has_rated}
                className={`focus:outline-none transition-all duration-200 ${
                  !productReview?.user_has_rated ? "hover:scale-110" : ""
                }`}
                onMouseEnter={() => {
                  if (productReview?.user_has_rated) {
                    return;
                  }
                  setHoveredRating(star);
                }}
                onMouseLeave={() => {
                  if (productReview?.user_has_rated) {
                    return;
                  }
                  setHoveredRating(0);
                }}
                onClick={() => {
                  if (productReview?.user_has_rated) {
                    return;
                  }
                  setRating(star);
                }}
              >
                <svg
                  className={`size-8 transition-colors ${
                    star <= activeRating
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300"
                  } ${
                    productReview?.user_has_rated ? "cursor-not-allowed" : ""
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </button>
            );
          })}
        </div>
        <div className="ml-3 text-sm text-gray-600">
          {rating > 0 || productReview?.user_has_rated ? (
            <span className="font-medium">
              {productReview?.user_has_rated
                ? productReview?.user_rating
                : rating}{" "}
              out of 5 stars
              {rating === 1 && " - Poor"}
              {rating === 2 && " - Fair"}
              {rating === 3 && " - Good"}
              {rating === 4 && " - Very Good"}
              {rating === 5 && " - Excellent"}
            </span>
          ) : null}
        </div>
      </div>
      <label htmlFor="review" className="text-gray-500 text-sm mt-2">
        Write a review
      </label>
      <textarea
        value={
          productReview?.user_has_rated
            ? productReview?.user_review
            : reviewComment
        }
        id="review"
        disabled={productReview?.user_has_rated}
        onChange={(e) => {
          if (!productReview?.user_has_rated) {
            setReviewComment(e.target.value);
          }
        }}
        placeholder="Share your experience with this product. What did you like or dislike about it?"
        className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none transition-all duration-200 disabled:text-gray-400 disabled:bg-gray-100"
        rows={5}
        maxLength={100}
      />
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-gray-500">
          {reviewComment.length}/100 characters
        </span>
        <span className="text-xs text-gray-400">Optional</span>
      </div>
      <button
        type="button"
        onClick={() => handleSubmitReview(productId)}
        disabled={isSubmitting || rating === 0 || productReview.user_has_rated}
        className="mr-auto mt-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Submitting...
          </div>
        ) : (
          "Submit Review"
        )}
      </button>
    </>
  );
};

const ExchangeSection = ({ orderId, orderDate, setExchangeInitiated }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState(false);
  const dispatch = useDispatch();
  const [isExchangeEligible, setIsExchangeEligible] = useState(false);

  const {
    data: exchangeOrderData,
    loading,
    // error,
  } = useSelector((state) => state.exchangeOrder);

  const handleExchange = async (e) => {
    e.preventDefault();
    if (reason.trim().length === 0) {
      setError(true);
      return;
    } else {
      setError(false);
    }

    if (!isExchangeEligible) {
      Alert.Alert.ErrorAlert("Not Eligible For Exchange");
    }
    try {
      const payload = {
        order_id: orderId,
        reason: reason.trim(),
        action: "exchange",
      };

      await exchangeOrder(dispatch, payload);
      setExchangeInitiated(true);
    } catch (error) {}
  };

  useEffect(() => {
    const date = new Date(orderDate);
    const now = new Date();
    // 15 days in milliseconds
    const FIFTEEN_DAYS_MS = 15 * 24 * 60 * 60 * 1000;

    // Check if current time is within 15 days from createdAt
    if (now.getTime() <= date.getTime() + FIFTEEN_DAYS_MS) {
      setIsExchangeEligible(true);
    } else {
      setIsExchangeEligible(false);
    }
  }, []);
  return (
    <form className="px-4" onSubmit={handleExchange}>
      <p className="text-gray-500 text-sm">Exchange this product</p>
      <label htmlFor="reason" className="required text-gray-500 text-sm mt-2">
        Reason
      </label>
      <input
        placeholder="Enter the reson for exchange"
        value={reason}
        disabled={!isExchangeEligible}
        required
        onChange={(e) => setReason(e.target.value)}
        className="w-full mt-2 px-4 py-3 border ring-0 outline-none  border-gray-300 rounded-lg resize-none transition-all duration-200 disabled:cursor-not-allowed disabled:text-gray-400 disabled:bg-gray-100"
      />
      {error && <p className="text-xs text-red-500">Required</p>}

      <button
        disabled={!isExchangeEligible || !reason.trim().length > 0 || loading}
        type="submit"
        className="mr-auto mt-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
      >
        Exchange
      </button>
    </form>
  );
};
